/**
 * Подгон листа документа под ширину экрана.
 *
 * Счёт, акт и наряд свёрстаны под А4: таблицы в них с фиксированными долями
 * колонок, и сжимать их вёрсткой нельзя — текст рассыпается по слогам,
 * а рамки перестают совпадать с бланком, по которому эти документы и сверяют.
 *
 * Поэтому лист не сжимается, а УМЕНЬШАЕТСЯ ЦЕЛИКОМ, как страница в
 * просмотрщике PDF. На телефоне видно весь лист сразу: реквизиты, таблицу,
 * подписи — по нему и проверяют, туда ли всё встало. Читать подробности
 * удобнее в PDF, кнопка рядом.
 *
 * До этого лист просто вылезал за края окна. Хуже всего, что незаметно:
 * обрезанное не выглядит обрезанным — колонка «Дата сдачи» показывала
 * «13.09.20» и казалась просто странной.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

export function FitToWidth({
  naturalWidth,
  fit = true,
  className,
  style,
  children,
}: {
  /** Ширина, на которую свёрстан лист. */
  naturalWidth: number;
  /**
   * Ужимать ли лист под ширину. `false` — лист в натуральную величину, а
   * место вокруг него листается вбок (обёртке нужен `overflow-x-auto`).
   *
   * Нужно там, где в листе ПРАВЯТ: ужатые до трети поля ввода на телефоне
   * получались с текстом в три пикселя, и браузер при нажатии сам приближал
   * страницу — окно съезжало, края обрезались.
   */
  fit?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const fit = () => {
      /*
        Считаем по СОДЕРЖИМОМУ, а не по `clientWidth`: последний включает
        внутренние отступы рамки. С ними лист получался шире места, которое
        ему на самом деле отведено, и правая колонка — «Цена» в наряде —
        оказывалась срезана ровно на величину отступа.
      */
      const style = getComputedStyle(box);
      const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const available = box.clientWidth - (Number.isFinite(padding) ? padding : 0);
      // Увеличивать не нужно: на широком экране лист показывается
      // в натуральную величину, как он и напечатается.
      if (available > 0) setScale(Math.min(1, available / naturalWidth));
    };

    fit();
    // ResizeObserver есть не везде; без него остаётся размер при открытии.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, [naturalWidth]);

  return (
    <div ref={boxRef} className={className} style={style}>
      <div
        /*
          `zoom`, а не `transform: scale`: последний уменьшает картинку, но
          оставляет под ней место прежнего размера — под листом появилась бы
          белая полоса высотой в половину экрана.
        */
        style={{ width: naturalWidth, zoom: fit ? scale : 1 }}
      >
        {children}
      </div>
    </div>
  );
}
